export function handleChatNavigationShortcut(
  event: KeyboardEvent,
  button: HTMLButtonElement | null,
  openPrevious: (() => void) | null,
  openNext: (() => void) | null,
): void {
  if ((event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') || event.defaultPrevented || event.repeat || event.isComposing
    || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return
  const open = event.key === 'ArrowLeft' ? openPrevious : openNext
  if (!open) return
  if (!button || button.disabled || !button.getClientRects().length) return

  const doc = button.ownerDocument
  if (!doc.defaultView?.matchMedia('(min-width: 768px) and (pointer: fine)').matches) return

  const ownsArrowKeys = 'input, textarea, select, [contenteditable]:not([contenteditable="false"]), '
    + '[role="textbox"], [role="combobox"], [role="slider"], [role="spinbutton"], '
    + '[role="tablist"], [role="listbox"], [role="tree"], [role="grid"], video, audio, .xterm'
  if ((event.target instanceof Element && event.target.closest(ownsArrowKeys))
    || doc.activeElement?.closest(ownsArrowKeys)) return
  if (doc.getSelection()?.isCollapsed === false) return
  if (Array.from(doc.querySelectorAll('dialog[open], [role="dialog"], [aria-modal="true"], [role="menu"]'))
    .some((overlay) => overlay.getClientRects().length > 0)) return

  // Backdrops without dialog semantics must also keep the underlying chat still.
  const rect = button.getBoundingClientRect()
  if (!button.contains(doc.elementFromPoint(rect.left + rect.width / 2, rect.top + rect.height / 2))) return

  event.preventDefault()
  open()
}

/** Delay navigation so a second press still acts on the original conversation. */
export function createF8Shortcut() {
  let pending: ReturnType<typeof setTimeout> | null = null
  let cooldownUntil = 0
  const cancel = () => {
    if (pending !== null) clearTimeout(pending)
    pending = null
  }
  return {
    cancel,
    handle(event: KeyboardEvent, anchor: HTMLButtonElement | null,
      openNext: (() => void) | null, complete: (() => void) | null) {
      if (event.key !== 'F8' || event.defaultPrevented || event.repeat || event.isComposing
        || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return
      if (!anchor || !anchor.getClientRects().length) return
      const doc = anchor.ownerDocument
      if (!doc.defaultView?.matchMedia('(min-width: 768px) and (pointer: fine)').matches) return
      if ((event.target instanceof Element && event.target.closest('.xterm'))
        || doc.activeElement?.closest('.xterm')) return
      if (Array.from(doc.querySelectorAll('dialog[open], [role="dialog"], [aria-modal="true"], [role="menu"]'))
        .some((overlay) => overlay.getClientRects().length > 0)) return
      const rect = anchor.getBoundingClientRect()
      if (!anchor.contains(doc.elementFromPoint(rect.left + rect.width / 2, rect.top + rect.height / 2))) return
      if (!openNext && !complete) return
      event.preventDefault()
      if (Date.now() < cooldownUntil) return
      if (pending !== null) {
        cancel()
        cooldownUntil = Date.now() + 300
        complete?.()
      } else {
        pending = setTimeout(() => {
          pending = null
          openNext?.()
        }, 300)
      }
    },
  }
}
